import { Media, Story } from '../../schema';
import { Gallery, Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { CommentSection } from './comments/commentSection';
import { InteractionsResponse, ProfileResponse } from '../../store/backendApi';
import { blurred, fullImage, thumbnail } from '../../utils/images';

const LazyImage = ({
  media,
  tripId,
  onImageLoad,
  photoSwipeRef,
}: {
  media: Media;
  tripId: string;
  onImageLoad: (imgElement: HTMLImageElement) => void;
  photoSwipeRef: (element: HTMLImageElement | null) => void;
}) => {
  const [loadingPhase, setLoadingPhase] = useState<'placeholder' | 'blurred' | 'thumbnail'>(
    'placeholder',
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Create an animated spinner SVG placeholder
  const createPlaceholderSVG = (width: number, height: number) => {
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <style>
            .spinner {
              animation: spin 1s linear infinite;
              transform-origin: center;
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          </style>
        </defs>
        <rect width="100%" height="100%" fill="#f8fafc"/>
        <circle cx="50%" cy="50%" r="30" fill="none" stroke="#e2e8f0" stroke-width="4"/>
        <circle cx="50%" cy="50%" r="30" fill="none" stroke="#3b82f6" stroke-width="4" 
                stroke-dasharray="60 188" stroke-dashoffset="0" stroke-linecap="round" 
                class="spinner" style="transform-origin: center;"/>
        <circle cx="50%" cy="50%" r="20" fill="none" stroke="#e2e8f0" stroke-width="2"/>
        <circle cx="50%" cy="50%" r="20" fill="none" stroke="#60a5fa" stroke-width="2" 
                stroke-dasharray="40 125" stroke-dashoffset="0" stroke-linecap="round" 
                class="spinner" style="transform-origin: center; animation-duration: 0.8s;"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  useEffect(() => {
    if (!imgRef.current) return;

    // Observer for blurred images (1000px away)
    const blurredObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && loadingPhase === 'placeholder') {
            setLoadingPhase('blurred');
            blurredObserver.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '3000px 0px',
        threshold: 0.1,
      },
    );

    // Observer for thumbnails (200px away)
    const thumbnailObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && loadingPhase === 'blurred') {
            setLoadingPhase('thumbnail');
            thumbnailObserver.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '200px 0px',
        threshold: 0.1,
      },
    );

    // Start observing
    blurredObserver.observe(imgRef.current);
    thumbnailObserver.observe(imgRef.current);

    return () => {
      blurredObserver.disconnect();
      thumbnailObserver.disconnect();
    };
  }, [loadingPhase]);

  // Always provide the ref to PhotoSwipe, regardless of loading state
  useEffect(() => {
    if (imgRef.current) {
      photoSwipeRef(imgRef.current);
    }
    return () => {
      photoSwipeRef(null);
    };
  }, [photoSwipeRef]);

  const url = (path: string) =>
    'https://' + `media.${process.env.REACT_APP_HOST}/${path}`.replaceAll('//', '/');

  const getImageSrc = () => {
    switch (loadingPhase) {
      case 'placeholder':
        return createPlaceholderSVG(200, 200); // Default dimensions
      case 'blurred':
        return url(blurred(`${tripId}/${media.id}.${media.ext}`));
      case 'thumbnail':
        return url(thumbnail(`${tripId}/${media.id}.${media.ext}`));
      default:
        return createPlaceholderSVG(200, 200);
    }
  };

  return (
    <img
      alt={media.caption}
      ref={imgRef}
      className="h-[200px] w-auto sm:max-w-none max-w-[40vw] rounded-xl object-cover cursor-pointer
      transition-opacity duration-300"
      style={{
        opacity: isLoaded ? 1 : 0.7,
        filter: loadingPhase === 'thumbnail' && isLoaded ? 'none' : 'blur(2px)',
      }}
      src={getImageSrc()}
      onLoad={() => {
        if (loadingPhase === 'thumbnail' && imgRef.current) {
          setIsLoaded(true);
          onImageLoad(imgRef.current);
        }
      }}
    />
  );
};

export const StoryBox = (props: {
  story: Story;
  interactions: InteractionsResponse | undefined;
  anchor: string;
  linkSecret: string | null;
  profile?: ProfileResponse | null;
  isOwner: boolean;
}) => {
  const [locale, setLocale] = useState<string | undefined>();

  useEffect(() => {
    setLocale(navigator.language);
  }, []);

  const url = (path: string) =>
    'https://' + `media.${process.env.REACT_APP_HOST}/${path}`.replaceAll('//', '/');

  const date =
    !locale || !locale.startsWith('de')
      ? format(new Date(props.story.date), 'MMMM d, yyyy', { locale: enUS })
      : format(new Date(props.story.date), 'd. MMMM yyyy', { locale: de });

  const scaleDimensions = (width: number, height: number) =>
    height > 768 ? { width: Math.round(width * (768 / height)), height: 768 } : { width, height };

  const comments =
    props.interactions?.stories.find((st) => st.storyId === props.story.id)?.comments ?? [];

  return (
    <div className="mx-auto max-w-7xl sm:px-6 lg:px-8 mt-12">
      <hr className="w-[80%] mx-auto h-px my-4 bg-gray-200 border-0 dark:bg-gray-700" />
      <div className="flex justify-center items-center space-x-2 text-sm">
        <p>{date}</p>
        {props.story.place && <span>&bull;</span>}
        {props.story.place && <p>{props.story.place}</p>}
      </div>
      <h2
        id={props.anchor}
        className="mt-1 text-2xl/7 text-center font-extrabold cursor-pointer scroll-m-40"
        onClick={() => (window.location.hash = props.anchor)}
      >
        {props.story.title}
      </h2>
      <p className="mt-4 text-md text-center whitespace-pre-line">{props.story.description}</p>
      <div className="mt-6 flex flex-wrap gap-1 justify-center">
        <Gallery id={props.story.id} withCaption={true}>
          {props.story.medias.map((media) => {
            const dim = scaleDimensions(media.dimensions.width, media.dimensions.height);
            return (
              <Item
                key={media.id}
                original={url(fullImage(`${props.story.tripId}/${media.id}.${media.ext}`))}
                thumbnail={url(thumbnail(`${props.story.tripId}/${media.id}.${media.ext}`))}
                width={dim.width}
                height={dim.height}
                caption={media.caption}
              >
                {({ ref, open }) => (
                  <div onClick={open}>
                    <LazyImage
                      media={media}
                      tripId={props.story.tripId}
                      photoSwipeRef={ref}
                      onImageLoad={(imgElement) => {
                        // This is now handled by the useEffect in LazyImage
                      }}
                    />
                  </div>
                )}
              </Item>
            );
          })}
        </Gallery>
      </div>

      {/* Like and Comments buttons */}
      <div className="mt-6 flex justify-center">
        <CommentSection
          tripId={props.story.tripId}
          storyId={props.story.id}
          comments={comments}
          linkSecret={props.linkSecret}
          profile={props.profile}
          interactions={props.interactions}
          isOwner={props.isOwner}
        />
      </div>
    </div>
  );
};
